// src/components/HelpModal.js
import React from 'react';
import { IoClose } from 'react-icons/io5';
import LengthRow from './LengthRow';

// A small, local component for displaying a single letter box for examples.
const LetterBox = ({ children, status = 'base' }) => {
    const styleMap = {
        base: 'bg-white dark:bg-dark-key-bg shadow-md',
        new: 'bg-accent dark:bg-dark-accent text-background dark:text-dark-background',
        empty: 'border-2 border-dashed border-gray-300 dark:border-gray-600',
        goal: 'bg-green-200 dark:bg-green-800 border-2 border-green-400 dark:border-green-600',
    };
    
    return (
        <div className={`font-bold uppercase flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-md text-xl sm:text-2xl ${styleMap[status]}`}>
            {children}
        </div>
    );
};

// A component to structure each rule's explanation.
const RuleSection = ({ title, description, children }) => (
    <div className="border-b border-gray-200 dark:border-gray-700 py-4">
        <h3 className="font-bold text-lg mb-2">{title}</h3>
        <p className="text-gray-600 dark:text-gray-300 mb-4">{description}</p>
        <div className="flex justify-center items-center">
            {children}
        </div>
    </div>
);


const HelpModal = ({ isOpen, onClose }) => {
    if (!isOpen) return null;

    return (
        // Modal Overlay
        <div 
            className="fixed inset-0 bg-black bg-opacity-75 flex justify-center items-center z-50 p-4"
            onClick={onClose}
        >
            {/* Modal Content */}
            <div 
                className="bg-background dark:bg-dark-background text-foreground dark:text-dark-foreground rounded-lg p-6 w-full max-w-lg mx-auto shadow-xl max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside the modal
            >
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-2xl font-bold">How to Play</h2>
                    <button onClick={onClose} className="text-3xl font-light">&times;</button>
                </div>

                <div className="space-y-4">
                    <RuleSection
                        title="1. Start with the daily word"
                        description="Each day begins with a 3-letter starting word. All players get the same one."
                    >
                        <div className="flex space-x-2">
                            <LetterBox>R</LetterBox>
                            <LetterBox>E</LetterBox>
                            <LetterBox>L</LetterBox>
                        </div>
                    </RuleSection>
                    
                    <RuleSection
                        title="2. Build the pyramid"
                        description="Form a new word that is one letter longer, using all letters from the previous word plus ONE new letter."
                    >
                        <div className="flex flex-col items-center space-y-2">
                            <div className="flex space-x-2">
                                <LetterBox>R</LetterBox>
                                <LetterBox>E</LetterBox>
                                <LetterBox>L</LetterBox>
                            </div>
                            <div className="text-2xl">↓</div>
                            <div className="flex space-x-2">
                                <LetterBox>R</LetterBox>
                                <LetterBox>E</LetterBox>
                                <LetterBox>L</LetterBox>
                                <LetterBox status="new">Y</LetterBox>
                            </div>
                        </div>
                    </RuleSection>
                    
                     <RuleSection
                        title="3. You can also Anagram"
                        description="You can rearrange the letters of the previous word when adding your new letter. The key is to use all the same letters, plus one new one."
                    >
                         <div className="flex flex-col items-center space-y-2">
                           <div className="flex space-x-2">
                                <LetterBox>R</LetterBox>
                                <LetterBox>E</LetterBox>
                                <LetterBox>L</LetterBox>
                                <LetterBox>Y</LetterBox>
                            </div>
                            <div className="text-2xl">↓</div>
                            <div className="flex space-x-2">
                                <LetterBox>L</LetterBox>
                                <LetterBox>A</LetterBox>
                                <LetterBox status="new">Y</LetterBox>
                                <LetterBox>E</LetterBox>
                                <LetterBox>R</LetterBox>
                            </div>
                        </div>
                    </RuleSection>

                    <RuleSection
                        title="4. Collect Medals"
                        description="As your word gets longer, you reach new Tiers and earn Medals! The targets are based on the Community Best."
                    >
                        <div className="grid grid-cols-2 gap-4 w-full text-center">
                            <div className="bg-gray-100 dark:bg-gray-800 p-2 rounded">
                                <span className="text-2xl">🥉</span>
                                <p className="font-bold">Bronze</p>
                                <p className="text-xs">40% of Community Best</p>
                            </div>
                            <div className="bg-gray-100 dark:bg-gray-800 p-2 rounded">
                                <span className="text-2xl">🥈</span>
                                <p className="font-bold">Silver</p>
                                <p className="text-xs">60% of Community Best</p>
                            </div>
                            <div className="bg-gray-100 dark:bg-gray-800 p-2 rounded">
                                <span className="text-2xl">🥇</span>
                                <p className="font-bold">Gold</p>
                                <p className="text-xs">80% of Community Best</p>
                            </div>
                            <div className="bg-gray-100 dark:bg-gray-800 p-2 rounded">
                                <span className="text-2xl">🏆</span>
                                <p className="font-bold">Platinum</p>
                                <p className="text-xs">Match Community Best</p>
                            </div>
                        </div>
                    </RuleSection>

                    <RuleSection
                        title="5. Community Best & Hints"
                        description="The Community Best is the longest word found by any player today. If you're stuck, you can Reveal the word, but this will LOCK your medals for the day (you can't earn better ones)."
                    >
                        <div className="flex flex-col items-center space-y-4 mt-4">
                            {/* Visual length display */}
                            <div className="flex flex-col items-center">
                                <p className="font-semibold text-center mb-2">TARGET: (7)</p>
                                <LengthRow length={7} size="small" />
                            </div>
                        </div>
                    </RuleSection>
                </div>
            </div>
        </div>
    );
};

export default HelpModal;
